// lang-boot — which dictionary to fetch before the first render.
//
// F-08 made the dictionaries lazy, which means the app now has to decide
// its language BEFORE it has rendered anything. That decision used to be
// made inside AppShell, from localStorage, on the first render — too
// late to matter and too late to await.
//
// The order below is the same one AppShell used, with one addition the
// old code could not make: a reader who has never opened the app has no
// stored language, and answering that with English when their browser
// says Hebrew was only ever defensible because switching was free. It is
// not free any more — it is a second download — so the guess is made
// properly, once, up front.
//
// Pure: no React, no imports from the app. Unit-tested in node.

import type { Lang } from '@nayto/i18n';

export const KNOWN_LANGS: readonly Lang[] = ['en', 'he', 'hy', 'ru'];

export const isLang = (s: string): s is Lang => (KNOWN_LANGS as readonly string[]).includes(s);

/** The key AppShell's `useLocalStorageState` uses. Named here so the two
 * readers of it cannot drift apart. */
export const LANG_STORAGE_KEY = 'sa_lang';

export const DEFAULT_LANG: Lang = 'en';

/**
 * Map a BCP-47 tag to one of ours: `he-IL` → `he`, `hy` → `hy`,
 * `ru-RU` → `ru`. Anything else — including `iw`, the deprecated code
 * some browsers still emit for Hebrew — is handled explicitly rather
 * than falling through to English.
 */
export function langFromTag(tag: string | null | undefined): Lang | null {
  if (!tag) return null;
  const primary = tag.toLowerCase().split('-')[0];
  if (primary === 'iw') return 'he'; // pre-1989 code, still in the wild
  return isLang(primary) ? primary : null;
}

/**
 * The language to load before the first paint.
 *
 *   1. What the reader last chose. This is authoritative — they picked it.
 *   2. What their browser asks for, first match across `languages`.
 *   3. English.
 *
 * `nav` and `storage` are injectable so this is testable without a DOM.
 */
export function initialLang(
  storage?: Pick<Storage, 'getItem'> | null,
  nav?: { languages?: readonly string[]; language?: string } | null,
): Lang {
  try {
    const stored = storage?.getItem(LANG_STORAGE_KEY);
    if (stored && isLang(stored)) return stored;
  } catch {
    // Private mode, blocked storage. Fall through to the browser.
  }
  const tags = nav?.languages ?? (nav?.language ? [nav.language] : []);
  for (const tag of tags) {
    const l = langFromTag(tag);
    if (l) return l;
  }
  return DEFAULT_LANG;
}

/** Read what the live browser says. Split out so `initialLang` stays pure. */
export function detectInitialLang(): Lang {
  const storage = typeof window === 'undefined' ? null : window.localStorage;
  const nav = typeof navigator === 'undefined' ? null : navigator;
  return initialLang(storage, nav);
}
